import addonsApi, { useGetAddonsDataQuery, useUpdateAddonsDataMutation } from '@/src/api/rtkQuery/addonsApi'
import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import { useAppDispatch } from '@/src/redux/store'
import { AddonsType } from '@/src/types/addons'
import { isEmpty } from '@/src/utils/generalUtils'
import { getAncillaryUpdatePayload } from '@/src/utils/itinerary/updatePayloadHelper'
import { takeScreenshot } from '@/src/utils/screenshot'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useSmbMandatoryCheck from '../useSmbMandatoryCheck'

const useAddonsCommon = () => {
    const dispatch = useAppDispatch()
    const itineraryId = useGetItineraryId()
    const { showError } = useThemedToast()
    const t = useTranslations('itineraryPage.step')
    const tCommon = useTranslations('common')
    const { data: addonsData, isLoading, isFetching, isError, isUninitialized } = useGetAddonsDataQuery({ itineraryId })
    const { flights, fareFamilyFares, meta, searchCriteria } = useGetItineraryDataQuery(
        {
            itineraryId
        },
        {
            selectFromResult: ({ data }) => ({
                faresList: data?.faresList || [],
                fareFamilyEnabledOnReview: data?.meta?.fareFamilyEnabledOnReview,
                fareFamilyFares: data?.fareFamilyFares || [],
                meta: data?.meta,
                searchCriteria: data?.searchCriteria,
                flights: data?.flights || []
            })
        }
    )
    const { isSeatMandatory, isMealMandatory } = useSmbMandatoryCheck()
    const [updateAddonsData] = useUpdateAddonsDataMutation()
    const isAncillaryApiLoading = isLoading || isFetching || isUninitialized

    const { paxInfo } = searchCriteria || {}
    const paxCount = (paxInfo?.adults || 0) + (paxInfo?.children || 0)

    const { ancillaryStatus, selectedAncillaries = [], journeyMap = [], journeyAncillaries } = addonsData || {}
    const availableAddons: AddonsType[] = useMemo(() => {
        const { isBaggageAvailable, isMealAvailable, isSeatAvailable } = ancillaryStatus || {}
        const availableAddons: AddonsType[] = []
        if (isSeatAvailable) availableAddons.push('SEAT')
        if (isMealAvailable) availableAddons.push('MEAL')
        if (isBaggageAvailable) availableAddons.push('BAGGAGE')

        return availableAddons
    }, [ancillaryStatus])

    const updateAncillaryUpdateFlag = (flag: boolean) => {
        dispatch(
            addonsApi.util.updateQueryData('getAddonsData', { itineraryId }, draft => {
                draft.isAncillaryUpdated = flag
            })
        )
    }

    const checkForMandatoryAncillarySelection = (ancillaryType: 'SEAT' | 'MEAL') => {
        const ancillaryKey = ancillaryType === 'SEAT' ? 'seatAncillaries' : 'mealAncillaries'
        const selectedAncillaryKey = ancillaryType === 'SEAT' ? 'seatDetails' : 'mealDetails'
        const availableSegments = journeyAncillaries?.[ancillaryKey]?.availableSegments || []
        let areAllAncillariesSelected = true
        if ((ancillaryType === 'SEAT' && !isSeatMandatory) || (ancillaryType === 'MEAL' && !isMealMandatory)) {
            return true
        }
        if (!isEmpty(availableSegments)) {
            availableSegments.forEach(segment => {
                const segmentData = selectedAncillaries.find(({ segmentOriginDestinationInfo }) => {
                    return (
                        segmentOriginDestinationInfo.from === segment.from &&
                        segmentOriginDestinationInfo.to === segment.to
                    )
                })
                if (segmentData) {
                    const selectedAncillaryCount = segmentData[selectedAncillaryKey]?.length
                    if (selectedAncillaryCount < paxCount) {
                        areAllAncillariesSelected = false
                    }
                } else {
                    areAllAncillariesSelected = false
                }
            })
        }
        if (!areAllAncillariesSelected) {
            showError(
                tCommon('mandatoryAncillaryErrorMessage', {
                    ancillaryName: tCommon(ancillaryType.toLocaleLowerCase())
                }),
                {
                    id: `mandatory-smb-${ancillaryType}-error`
                }
            )
        }
        return areAllAncillariesSelected
    }

    const updateAddonsHandler = useCallback(
        async ({
            isOptimisticSuccessUpdate,
            onMandatoryAddonsCheckFailure,
            onSuccess,
            onFailure
        }: {
            isOptimisticSuccessUpdate: boolean
            onMandatoryAddonsCheckFailure: () => void
            onSuccess: () => void
            onFailure: () => void
        }) => {
            const payload = getAncillaryUpdatePayload({
                journeyMap,
                selectedAncillaries
            })

            if (isSeatMandatory || isMealMandatory) {
                if (isAncillaryApiLoading) {
                    showError(tCommon('plzWaitForAncillaryApi'), {
                        id: 'mandatory-smb-api-error'
                    })
                    onMandatoryAddonsCheckFailure?.()
                    return
                }

                if (isSeatMandatory) {
                    const isSeatSelectedForAllSegmentsAndPax = checkForMandatoryAncillarySelection('SEAT')
                    if (!isSeatSelectedForAllSegmentsAndPax) {
                        onMandatoryAddonsCheckFailure?.()
                        return
                    }
                }
                if (isMealMandatory) {
                    const isMealSelectedForAllSegmentsAndPax = checkForMandatoryAncillarySelection('MEAL')
                    if (!isMealSelectedForAllSegmentsAndPax) {
                        onMandatoryAddonsCheckFailure?.()
                        return
                    }
                }
            }

            takeScreenshot(itineraryId, 'addons', flights)

            if (isOptimisticSuccessUpdate) {
                updateAncillaryUpdateFlag(true)
                onSuccess?.()
            }

            const response = await updateAddonsData({ itineraryId, payload })
            if (response.error || !response.data?.status) {
                // API failed, rollback to previous step
                updateAncillaryUpdateFlag(false)
                onFailure?.()
                showError('Failed to update addons', {
                    id: 'update-addons-error'
                })
                return
            } else if (!isOptimisticSuccessUpdate) {
                updateAncillaryUpdateFlag(true)
                onSuccess?.()
            }
        },
        [journeyMap, selectedAncillaries, isSeatMandatory, isMealMandatory]
    )

    return {
        isLoading: isAncillaryApiLoading,
        isError,
        itineraryId,
        stepTitle: t('addons'),
        availableAddons,
        isSeatMandatory,
        isMealMandatory,
        checkForMandatoryAncillarySelection,
        updateAddonsHandler
    }
}

export default useAddonsCommon
